import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  arrayUtils,
  Body,
  Headline,
  LinkButton,
  ListItem,
} from '@sato/components';
import { observer } from 'mobx-react-lite';

import { StaticContentfulEntry } from '@/contentful/contentful.constants';
import { useContentfulEntry } from '@/contentful/hooks/useContentful';
import { IWidget } from '@/contentful/types';

import { Link } from '@components/common/Link';
import { HookFormRadioTileGroup } from '@components/ReactHookFormFields/HookFormRadioTileGroup';
import { HookFormTextArea } from '@components/ReactHookFormFields/HookFormTextArea';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { useOnWindowFocus } from '@hooks/useOnWindowFocus';
import {
  ApartmentAccessMethod,
  DefectIncidentRequest,
  PetsInformation,
} from '@models/api';
import { ROUTES } from '@routes/routes.constants';
import { userStore } from '@stores/userStore';

export const ApartmentAccessField = observer(() => {
  const { data } = useContentfulEntry<IWidget>(
    StaticContentfulEntry.APARTMENT_ACCESS_COMMON_FORM
  );
  const { t, tContentful } = useCustomTranslations(data?.customTranslations);

  const formContext = useFormContext<DefectIncidentRequest>();
  const apartmentAccessValue = formContext.watch('access');

  // Refetch user data on window focus so that phone numbers are updated immediately when returning from MyInformation page
  useOnWindowFocus(userStore.refreshUser);

  return (
    <div className="mt-3 flex flex-col gap-5 border-y border-black-light py-5">
      <div>
        <Headline level={3} className="mb-3">
          {t('apartmentAccessSelectionTitle')}
        </Headline>

        <HookFormRadioTileGroup
          name="access"
          items={[
            { title: t('apartmentAccessOk'), value: ApartmentAccessMethod.OK },
            {
              title: t('apartmentAccessAgreeOnTime'),
              value: ApartmentAccessMethod.AGREE_ON_TIME,
            },
          ]}
          groupClasses="grid !gap-2"
          staticFieldMessageSpace={false}
        />

        <Body>{t('apartmentAccessMessage')}</Body>

        {apartmentAccessValue === ApartmentAccessMethod.AGREE_ON_TIME && (
          <ListItem label={tContentful('your_phone')} className="w-full">
            <div className="flex flex-wrap gap-3">
              {arrayUtils.joinArrayString(
                [
                  userStore.user?.phoneNumber,
                  userStore.user?.secondaryPhoneNumber,
                ],
                ', '
              )}

              <LinkButton
                linkComponent={Link}
                to={ROUTES.MY_INFORMATION}
                variant="text"
                openInNewTab
                icon="edit"
                iconPosition="right"
              >
                {tContentful('edit_from_my_information_page')}
              </LinkButton>
            </div>
          </ListItem>
        )}
      </div>

      <HookFormRadioTileGroup
        name="pets"
        label={t('petsTitle')}
        items={[
          { title: tContentful('yes'), value: PetsInformation.YES },
          { title: tContentful('no'), value: PetsInformation.NO },
        ]}
        groupClasses="grid !gap-2"
        staticFieldMessageSpace={false}
      />

      <HookFormTextArea
        name="info"
        label={{ value: t('infoLabel'), type: 'static' }}
        placeholder={t('infoPlaceholder')}
        rows={6}
        type="text"
        maxLength={1800}
      />
    </div>
  );
});
